import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { Assignment } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
} from '@mui/material'

import {
  deleteEntraIdRolesAsync,
  upsertEntraIdRolesAsync,
} from '../../api/entraIdRolesApi'
import {
  EntraIdRoleRequestDto,
  EntraIdRoleResponseDto,
} from '../../api/kmsapiclient'
import { usePermissions } from '../../components/common/permission/usePermissions'
import {
  apiCallError,
  beginApiCall,
  endApiCall,
} from '../../redux/actions/apiStatusActions'
import {
  deleteEntraIdRolesSuccess,
  upsertEntraIdRolesSuccess,
} from '../../redux/actions/entraIdRolesActions'
import { useAppDispatch } from '../../redux/configureStore'
import { IRootState } from '../../redux/reducers'
import {
  InsertSelectList,
  InsertSelectListPropsRef,
  ListOption,
} from '../common/InsertSelectList'
import { isScreenSizeSmall } from '../common/utils'

import { EntraIdOperationRolesView } from './entraIdOperationRolesView'

export type EntraIdRolesListProps = {
  entraIdRoles: EntraIdRoleRequestDto[]
}

type EntraIdRolesView = EntraIdRoleRequestDto & {
  isDeleted: boolean
}

export const EntraIdRolesListView = ({
  entraIdRoles,
}: EntraIdRolesListProps) => {
  const [entraIdRolesList, setEntraIdRolesList] = useState<
    EntraIdRoleRequestDto[]
  >([])
  const insertSelectListRef = useRef<InsertSelectListPropsRef>(null)
  const [modifiedEntraIdRoles, setModifiedEntraIdRoles] = useState<
    EntraIdRolesView[]
  >([])

  const [selectedEntraIdRoles, setSelectedEntraIdRoles] = useState<
    EntraIdRoleRequestDto | undefined
  >(undefined)

  const [hasDuplicateEntraIdError, setHasDuplicateEntraIdError] =
    useState(false)
  const [hasValidEntraIdRoleType, setHasValidEntraIdRoleType] = useState(true)
  const [openResetConfirmation, setOpenResetConfirmation] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined,
  )
  const [defaultSelectedIndex, setDefaultSelectedIndex] = useState<
    number | undefined
  >(undefined)

  const identity = useSelector((state: IRootState) => state.identity)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { getUserPermissions } = usePermissions()

  useEffect(() => {
    if (entraIdRoles && entraIdRoles.length > 0) {
      setEntraIdRolesList(entraIdRoles)
      setSelectedIndex(0)
      setDefaultSelectedIndex(0)
      setSelectedEntraIdRoles({ ...entraIdRoles[0] })
    }
  }, [entraIdRoles])

  const upsertedEntraIdRoles = useMemo(() => {
    return modifiedEntraIdRoles
      .filter((entraIdRole) => !entraIdRole.isDeleted)
      .map((entraIdRole) => {
        return { ...entraIdRole } as EntraIdRoleRequestDto
      })
  }, [modifiedEntraIdRoles])

  const deletedEntraIdRoles = useMemo(() => {
    return modifiedEntraIdRoles
      .filter((entraIdRole) => entraIdRole.isDeleted && !!entraIdRole.id)
      .map((entraIdRole) => {
        return { ...entraIdRole } as EntraIdRoleRequestDto
      })
  }, [modifiedEntraIdRoles])

  const hasChanges = useMemo(() => {
    return (
      (upsertedEntraIdRoles && upsertedEntraIdRoles.length > 0) ||
      (deletedEntraIdRoles && deletedEntraIdRoles.length > 0)
    )
  }, [upsertedEntraIdRoles, deletedEntraIdRoles])

  const showMessages = useMemo(() => {
    return hasChanges || hasDuplicateEntraIdError || !hasValidEntraIdRoleType
  }, [hasChanges, hasDuplicateEntraIdError, hasValidEntraIdRoleType])

  const entraIdNames = useMemo(() => {
    if (entraIdRolesList && entraIdRolesList.length > 0) {
      return entraIdRolesList
        .map((entraIdRole) => entraIdRole.entraIdName)
        .join(',')
    }
    return null
  }, [entraIdRolesList])

  const entraIdRoleOptions = useMemo(() => {
    if (entraIdNames) {
      return entraIdRolesList.map((entraIdRole) => ({
        value: entraIdRole.id ?? entraIdRole.entraIdName.replace(/\s/g, ''),
        text: entraIdRole.entraIdName,
      }))
    }
    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entraIdNames])

  /**
   * Function to handle the selection of an Entra ID Group
   * @param selectedOption represents the selected Entra ID Group
   */
  const handleSelect = (selectedOption: ListOption | undefined) => {
    if (selectedOption) {
      const selected = entraIdRolesList.find(
        (entraIdRole) =>
          entraIdRole.entraIdName.toLowerCase() ===
          selectedOption.text.toLowerCase(),
      )
      if (selected) {
        setSelectedEntraIdRoles(selected)
      }
    } else {
      setSelectedEntraIdRoles(undefined)
    }
  }

  /**
   * Function to handle the change of text for an Entra ID Group
   * @param updatedOption represents the updated Entra ID Group
   */
  const handleTextChange = (updatedOption: ListOption) => {
    const entraIdRole = entraIdRolesList.find((entraIdRole) =>
      isSameEntraIdRoleGroup(selectedEntraIdRoles, entraIdRole),
    )

    if (entraIdRole) {
      const updatedEntraIdRole = {
        ...entraIdRole,
        entraIdName: updatedOption.text,
      }

      setSelectedEntraIdRoles(updatedEntraIdRole)

      setEntraIdRolesList((prevEntraIdRolesList) => {
        return prevEntraIdRolesList.map((prevEntraIdRoles) =>
          isSameEntraIdRoleGroup(selectedEntraIdRoles, prevEntraIdRoles)
            ? { ...prevEntraIdRoles, ...updatedEntraIdRole }
            : prevEntraIdRoles,
        )
      })

      setModifiedEntraIdRoles((prevModifiedEntraIdRoles) => {
        return prevModifiedEntraIdRoles && prevModifiedEntraIdRoles.length > 0
          ? prevModifiedEntraIdRoles.map((prevModifiedEntraIdRole) =>
              isSameEntraIdRoleGroup(
                selectedEntraIdRoles,
                prevModifiedEntraIdRole,
              )
                ? { ...prevModifiedEntraIdRole, ...updatedEntraIdRole }
                : prevModifiedEntraIdRole,
            )
          : [{ ...updatedEntraIdRole, isDeleted: false } as EntraIdRolesView]
      })
    }
  }

  /**
   * Function to handle the addition of a new Entra ID Group
   * @param {ListOption} newOption represents the new Entra ID Group
   * @param {number} index represents the index of the new Entra ID Group
   */
  const handleEntraIdRoleAdded = (newOption: ListOption, index: number) => {
    const newEntraIdRole: EntraIdRoleRequestDto = {
      entraIdName: newOption.text,
      createKey: false,
      rotateKey: false,
      getKey: false,
      deleteKey: false,
      getPermission: false,
      addPermission: false,
      updatePermission: false,
      deletePermission: false,
      entraIDGroupRoles: false,
      createSecret: false,
      getSecret: false,
      deleteSecret: false,
    }

    setModifiedEntraIdRoles([
      ...modifiedEntraIdRoles,
      { ...newEntraIdRole, isDeleted: false } as EntraIdRolesView,
    ])

    setSelectedIndex(index)
    setEntraIdRolesList([...entraIdRolesList, newEntraIdRole])
    setSelectedEntraIdRoles(newEntraIdRole)
  }

  /**
   * Function to handle the deletion of an Entra ID Group *
   * @param {ListOption} deletedOption represents the deleted Entra ID Group
   */
  const handleEntraIdRoleDeleted = (deletedOption: ListOption) => {
    const deletedEntraIdRole = entraIdRolesList.find(
      (entraIdRole) =>
        entraIdRole.entraIdName.toLowerCase() ===
        deletedOption?.text.toLowerCase(),
    )

    if (deletedEntraIdRole) {
      setModifiedEntraIdRoles([
        ...modifiedEntraIdRoles.filter(
          (entraIdRole) =>
            !isSameEntraIdRoleGroup(deletedEntraIdRole, entraIdRole),
        ),
        { ...deletedEntraIdRole, isDeleted: true } as EntraIdRolesView,
      ])
      setEntraIdRolesList(
        entraIdRolesList.filter(
          (entraIdRole) => entraIdRole.entraIdName !== deletedOption.text,
        ),
      )
      if (
        selectedEntraIdRoles &&
        selectedEntraIdRoles.entraIdName.toLowerCase() ===
          deletedOption.text.toLowerCase()
      ) {
        setSelectedEntraIdRoles(undefined)
      }
    }
  }

  /**
   * Function to handle the change of Entra ID Group Roles selection
   * @param {EntraIdRoleRequestDto} selectedEntraIdRoles represents the selected Entra ID Group Roles
   */
  const handleEntraIdOperationRolesChange = (
    selectedEntraIdRoles: EntraIdRoleRequestDto,
  ) => {
    setEntraIdRolesList((prevEntraIdRolesList) => {
      return prevEntraIdRolesList.map((prevEntraIdRoles) =>
        isSameEntraIdRoleGroup(selectedEntraIdRoles, prevEntraIdRoles)
          ? { ...prevEntraIdRoles, ...selectedEntraIdRoles }
          : prevEntraIdRoles,
      )
    })

    if (modifiedEntraIdRoles && modifiedEntraIdRoles.length > 0) {
      setModifiedEntraIdRoles((prevModifiedEntraIdRoles) => {
        return prevModifiedEntraIdRoles.map((prevModifiedEntraIdRole) =>
          isSameEntraIdRoleGroup(selectedEntraIdRoles, prevModifiedEntraIdRole)
            ? { ...prevModifiedEntraIdRole, ...selectedEntraIdRoles }
            : prevModifiedEntraIdRole,
        )
      })
    } else {
      setModifiedEntraIdRoles([
        ...modifiedEntraIdRoles,
        { ...selectedEntraIdRoles, isDeleted: false } as EntraIdRolesView,
      ])
    }

    setSelectedEntraIdRoles((prevEntraIdRoles) => {
      return {
        ...prevEntraIdRoles,
        ...selectedEntraIdRoles,
      }
    })
  }

  /**
   * Function to check whether the two Entra ID Groups are the same
   * @param {(EntraIdRoleRequestDto | null)} entraIdRoleGroup1 represents the first Entra ID Roles Group
   * @param {(EntraIdRoleRequestDto | null)} entraIdRoleGroup2 represents the second Entra ID Roles Group
   * @return {*}  {boolean} returns true if the two Entra ID Groups are the same
   */
  const isSameEntraIdRoleGroup = (
    entraIdRoleGroup1: EntraIdRoleRequestDto | undefined,
    entraIdRoleGroup2: EntraIdRoleRequestDto | undefined,
  ): boolean => {
    return (
      entraIdRoleGroup1 !== undefined &&
      entraIdRoleGroup2 !== undefined &&
      (((entraIdRoleGroup1.id !== undefined ||
        entraIdRoleGroup2.id !== undefined) &&
        entraIdRoleGroup1.id === entraIdRoleGroup2.id) ||
        entraIdRoleGroup1.entraIdName.toLowerCase() ===
          entraIdRoleGroup2.entraIdName.toLowerCase())
    )
  }

  /**
   * Function to reset the Entra ID Permissions
   */
  const resetEntraIdPermissions = (): void => {
    setEntraIdRolesList(entraIdRoles)
    setModifiedEntraIdRoles([])
    setHasDuplicateEntraIdError(false)
    setHasValidEntraIdRoleType(true)
    setSelectedEntraIdRoles(undefined)
    setSelectedIndex(defaultSelectedIndex)
    insertSelectListRef.current?.reset()
  }

  /**
   * Function to submit the Entra ID Roles
   */
  const submitEntraIdRoles = async () => {
    const isValid = validateEntraIdRoleType()
    if (!isValid) {
      return
    }

    dispatch(beginApiCall())
    try {
      const result: [EntraIdRoleResponseDto[] | null, string | null] =
        await Promise.all([
          upsertedEntraIdRoles && upsertedEntraIdRoles.length > 0
            ? upsertEntraIdRolesAsync(upsertedEntraIdRoles)
            : Promise.resolve(null),

          deletedEntraIdRoles && deletedEntraIdRoles.length > 0
            ? deleteEntraIdRolesAsync(deletedEntraIdRoles)
            : Promise.resolve(null),
        ])

      if (result) {
        const [upsertResult, deleteResult] = result
        if (upsertResult) {
          dispatch(upsertEntraIdRolesSuccess({ entraIdRoles: upsertResult }))
        }
        if (deleteResult) {
          dispatch(
            deleteEntraIdRolesSuccess({ entraIdRoles: deletedEntraIdRoles }),
          )
        }
        toast.success(t('entraid.submitted'))
      } else {
        dispatch(endApiCall())
        toast.error(t('entraid.submit_failed'))
      }
    } catch (error) {
      dispatch(apiCallError())
      throw error
    } finally {
      await getUserPermissions()
    }
    setModifiedEntraIdRoles([])
  }

  /**
   * Function to validate the Entra ID Role Type
   * @return {*}  {boolean} returns true if the Entra ID Role Type is valid
   */
  const validateEntraIdRoleType = (): boolean => {
    const hasValidEntraIdRoleType = entraIdRolesList.some((entraIdRole) => {
      return entraIdRole.entraIDGroupRoles === true
    })
    setHasValidEntraIdRoleType(hasValidEntraIdRoleType)
    return hasValidEntraIdRoleType
  }

  /**
   * Function to validate the Entra ID Group Name
   * @param {string} entraIdName represents the Entra ID Group Name
   * @param {boolean} [reset=false] represents whether to reset the validation
   * @return {*}  {boolean} returns true if the Entra ID Group Name is valid
   */
  const validateEntraIdName = (entraIdName: string, reset = false): boolean => {
    let isValid = true
    if (!reset) {
      if (!entraIdName) {
        isValid = false
      } else {
        isValid = entraIdRolesList.every(
          (entraIdRole) =>
            entraIdRole.entraIdName.toLowerCase() !== entraIdName.toLowerCase(),
        )
      }
    }

    setHasDuplicateEntraIdError(!isValid)
    return isValid
  }

  /**
   * Function to handle the reset confirmation
   */
  const handleResetConfirmation = (): void => {
    resetEntraIdPermissions()
    setOpenResetConfirmation(false)
  }

  const responsiveFontStyle = isScreenSizeSmall() ? 'large' : 'small'

  return (
    <>
      {showMessages && (
        <Stack direction="column" spacing={2}>
          {hasChanges && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize={responsiveFontStyle} />}
            >
              <div className="responsive-font">{t('entraid.unsaved')}</div>
            </Alert>
          )}

          {!hasValidEntraIdRoleType && (
            <Alert
              severity="error"
              icon={<ErrorOutlineIcon fontSize={responsiveFontStyle} />}
            >
              <div className="responsive-font">
                {t('entraid.entraid_group_required')}
              </div>
            </Alert>
          )}

          {hasDuplicateEntraIdError && (
            <Alert
              severity="error"
              icon={<ErrorOutlineIcon fontSize={responsiveFontStyle} />}
            >
              <div className="responsive-font">{t('entraid.group_exists')}</div>
            </Alert>
          )}
        </Stack>
      )}

      <Box
        sx={{
          border: 1,
          borderColor: 'grey.500',
          height: '55vh',
          maxHeight: '55vh',
        }}
      >
        <Grid
          container
          columnSpacing={isScreenSizeSmall() ? 0 : 2}
          rowSpacing={isScreenSizeSmall() ? 2 : 0}
          sx={{
            height: '100%',
            maxHeight: '100%',
          }}
        >
          <Grid
            item
            xs={12}
            md={isScreenSizeSmall() ? 12 : 6}
            lg={6}
            sx={{
              height: isScreenSizeSmall() ? '50%' : '100%',
              maxHeight: isScreenSizeSmall() ? '50%' : '100%',
            }}
          >
            <InsertSelectList
              disabled={identity === null}
              inputPlaceHolder={t('entraid.enter_group_name')}
              selectedIndex={selectedIndex}
              onSelect={handleSelect}
              onTextChange={handleTextChange}
              options={entraIdRoleOptions}
              onDelete={handleEntraIdRoleDeleted}
              onItemAdded={handleEntraIdRoleAdded}
              validateFn={validateEntraIdName}
              ref={insertSelectListRef}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={isScreenSizeSmall() ? 12 : 6}
            lg={6}
            sx={{
              borderLeft: isScreenSizeSmall() ? 0 : 1,
              borderTop: isScreenSizeSmall() ? 1 : 0,
              borderColor: 'grey.500',
              height: isScreenSizeSmall() ? '50%' : '100%',
              maxHeight: isScreenSizeSmall() ? '50%' : '100%',
            }}
            padding={2}
          >
            {selectedEntraIdRoles && (
              <Box sx={{ height: '100%', maxHeight: '100%' }}>
                <EntraIdOperationRolesView
                  selectedEntraIdRoles={selectedEntraIdRoles}
                  onEntraIdOperationRolesChange={
                    handleEntraIdOperationRolesChange
                  }
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box
        my={2}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          bottom: 0,
        }}
      >
        <Button
          onClick={() => {
            setOpenResetConfirmation(true)
          }}
          variant="contained"
          color="error"
          sx={{
            color: 'white',
            '&.Mui-disabled': {
              color: 'grey.500',
              backgroundColor: 'error.main',
            },
          }}
          disabled={!hasChanges}
        >
          <div className="responsive-font responsive-icon">
            <DeleteIcon fontSize={responsiveFontStyle} />
            {t('reset')}
          </div>
        </Button>
        <Button
          onClick={() => {
            void (async () => {
              await submitEntraIdRoles()
            })()
          }}
          className="btn-primary"
          variant="contained"
          disabled={!hasChanges}
          sx={{
            color: 'white',
            '&.Mui-disabled': {
              color: 'grey.500',
              backgroundColor: 'primary.main',
            },
          }}
        >
          <div className="responsive-font responsive-icon">
            <Assignment fontSize={responsiveFontStyle} />
            {t('submit')}
          </div>
        </Button>
      </Box>
      <Dialog
        open={openResetConfirmation}
        onClose={() => {
          setOpenResetConfirmation(false)
        }}
        aria-labelledby="reset-alert-dialog-title"
        aria-describedby="reset-alert-dialog-description"
      >
        <DialogTitle id="reset-alert-dialog-title">
          {t('entraid.reset')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-alert-dialog-description">
            {t('entraid.reset_confirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenResetConfirmation(false)
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleResetConfirmation}
            autoFocus
            variant="contained"
          >
            {t('ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
